import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, staggerContainer, cardItem } from '../lib/motion';

const STATUS_STYLES = {
  assigned: 'bg-amber-50 text-amber-700',
  accepted: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-purple-50 text-purple-700',
  completed: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-700',
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status?.replace('_', ' ')}
  </span>
);

// Full-page state used for pending / rejected / no-request / error —
// these all share the same centered, icon + message layout.
const StatusScreen = ({ icon, tone, title, message }) => {
  const toneStyles = {
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    red: 'bg-red-50 border-red-100 text-red-800',
  };
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`max-w-md w-full border rounded-xl p-7 text-center ${toneStyles[tone]}`}
      >
        <p className="text-3xl mb-3">{icon}</p>
        <p className="font-semibold mb-1.5">{title}</p>
        <p className="text-sm leading-relaxed opacity-90">{message}</p>
      </motion.div>
    </div>
  );
};

const EngineerDashboard = () => {
  const { user, updateUser } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [engineerRequestStatus, setEngineerRequestStatus] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get('/orders/engineer');
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch orders.');
    }
  }, []);

  useEffect(() => {
    let interval;
    const fetchEngineerStatusAndOrders = async () => {
      if (!user || user.role !== 'engineer') {
        setLoading(false);
        return;
      }

      try {
        const requestResponse = await axios.get('/auth/engineer-requests');
        if (requestResponse.data && requestResponse.data.length > 0) {
          const latestRequest = requestResponse.data[0];
          setEngineerRequestStatus(latestRequest.status);

          if (latestRequest.status === 'approved') {
            if (!user.hasSeenEngineerWelcome) {
              setShowWelcome(true);
            }
            await fetchOrders();

            // Poll every 30 seconds for new service request updates
            interval = setInterval(() => {
              fetchOrders();
            }, 30000);
          }
        } else {
          setEngineerRequestStatus('no_request');
        }
      } catch (error) {
        console.error('Error fetching engineer status or orders:', error);
        toast.error('Failed to load engineer dashboard data.');
        setEngineerRequestStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchEngineerStatusAndOrders();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, fetchOrders]);

  const markWelcomeAsSeen = async () => {
    try {
      await updateUser({ hasSeenEngineerWelcome: true });
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    markWelcomeAsSeen();
  };

  const handleAcceptOrder = async (orderId) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/orders/${orderId}/accept`);
      if (response.data.success) {
        toast.success('Order accepted and started!');
        fetchOrders();
      } else {
        toast.error(response.data.message || 'Failed to accept order.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept order.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/orders/${orderId}/complete`);
      if (response.data.success) {
        toast.success('Order completed successfully!');
        fetchOrders();
      } else {
        toast.error(response.data.message || 'Failed to complete order.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete order.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-6">
        <div className="max-w-5xl mx-auto animate-pulse flex flex-col gap-6">
          <div>
            <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
            <div className="h-8 bg-gray-200 rounded w-48" />
          </div>
          <div className="flex flex-col gap-4 mt-6">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-40 bg-white border border-gray-100 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // ── Pending ───────────────────────────────────────────────────────
  if (engineerRequestStatus === 'pending') {
    return (
      <StatusScreen
        icon="⏳"
        tone="amber"
        title="Application pending"
        message="Your engineer application is currently under review. You'll gain full access once it's approved by an administrator."
      />
    );
  }

  // ── Rejected ──────────────────────────────────────────────────────
  if (engineerRequestStatus === 'rejected') {
    return (
      <StatusScreen
        icon="✕"
        tone="red"
        title="Application not approved"
        message="Unfortunately, your engineer application wasn't approved this time. Please contact support for more information or consider reapplying."
      />
    );
  }

  // ── No request / Error ────────────────────────────────────────────
  if (engineerRequestStatus === 'no_request' || engineerRequestStatus === 'error') {
    return (
      <StatusScreen
        icon="⚠️"
        tone="red"
        title="Access issue"
        message="There was an issue verifying your engineer status. Please contact support."
      />
    );
  }

  // ── Main Dashboard ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* Header */}
        <motion.div className="mb-7" initial="hidden" animate="show" variants={fadeUp}>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Engineer</p>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
        </motion.div>

        {/* Welcome banner — shown once after approval */}
        <AnimatePresence>
          {showWelcome && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-green-50 border border-green-100 rounded-xl p-5 relative">
                <button
                  onClick={dismissWelcome}
                  aria-label="Dismiss welcome message"
                  className="absolute top-3 right-3 text-green-400 hover:text-green-700 transition-colors text-lg leading-none"
                >
                  &times;
                </button>
                <p className="font-semibold text-green-800 mb-1">Congratulations! 🎉</p>
                <p className="text-sm text-green-700 leading-relaxed pr-6">
                  Your engineer application has been approved. Welcome to the MV Computers team —
                  you now have access to assigned service requests below.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Service requests */}
        <div className="mb-10">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            My service requests ({orders.length})
          </p>

          {orders.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-10 text-center">
              <p className="text-3xl mb-3">🖨️</p>
              <p className="text-gray-500 text-sm">No service requests assigned to you yet.</p>
            </div>
          ) : (
            <motion.div
              className="flex flex-col gap-3"
              variants={staggerContainer}
              initial="hidden"
              animate="show"
            >
              {orders.map((order) => (
                <motion.div
                  key={order._id}
                  variants={cardItem}
                  className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{order.customer?.name || 'Customer'}</p>
                      <p className="text-xs text-gray-400">{order.customer?.email}</p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">Printer</p>
                      <p className="text-gray-800">{order.service?.printerModel || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">Service type</p>
                      <p className="text-gray-800">{order.service?.doorstep ? 'Doorstep' : 'In-shop'}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{order.service?.description}</p>

                  <div className="pt-3 border-t border-gray-50 flex justify-end">
                    {order.status === 'assigned' && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleAcceptOrder(order._id)}
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        Start service
                      </motion.button>
                    )}
                    {order.status === 'in_progress' && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleCompleteOrder(order._id)}
                        disabled={actionLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        Mark complete
                      </motion.button>
                    )}
                    {order.status === 'completed' && (
                      <span className="text-green-600 text-xs font-semibold">✓ Completed</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>

        {/* Quick links — placeholders for future engineer tools */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          {[
            { icon: '📅', title: 'Schedule', desc: 'Check your upcoming appointments and service schedules.', label: 'View schedule' },
            { icon: '🧰', title: 'Tools & resources', desc: 'Access diagnostic tools and technical resources.', label: 'Access tools' },
            { icon: '📊', title: 'Performance', desc: 'View your service completion stats and ratings.', label: 'View stats' },
          ].map(({ icon, title, desc, label }) => (
            <motion.div
              key={title}
              variants={cardItem}
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
            >
              <span className="text-2xl mb-3 block">{icon}</span>
              <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">{desc}</p>
              <button
                disabled
                title="Coming soon"
                className="text-xs font-medium text-gray-400 cursor-not-allowed"
              >
                {label} (coming soon)
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default EngineerDashboard;